import {
  Color,
  ShaderMaterial,
  Vector3,
  type CubeTexture,
  type Texture,
} from 'three';
import { CLOUD_LIGHTING_DEFAULTS, WATER_DEFAULTS } from '../skyStore';

export const createWaterMaterial = (
  normals: Texture,
  skyMap: CubeTexture
): ShaderMaterial =>
  new ShaderMaterial({
    uniforms: {
      normalMap: { value: normals },
      skyMap: { value: skyMap },
      minimumLight: { value: CLOUD_LIGHTING_DEFAULTS.minIrradiance },
      time: { value: 0 },
      size: { value: 1 },
      distortionScale: { value: WATER_DEFAULTS.distortion },
      roughness: { value: WATER_DEFAULTS.roughness },
      waterColor: { value: new Color(WATER_DEFAULTS.color) },
      sunColor: { value: new Color(0) },
      sunDirection: { value: new Vector3(0, 1, 0) },
      skyIrradiance: { value: new Vector3() },
    },
    vertexShader: /* glsl */ `
      varying vec3 worldPosition;
      #include <common>
      #include <logdepthbuf_pars_vertex>
      void main() {
        worldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * viewMatrix * vec4(worldPosition, 1.0);
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      uniform sampler2D normalMap;
      uniform samplerCube skyMap;
      uniform float minimumLight;
      uniform float time;
      uniform float size;
      uniform float distortionScale;
      uniform float roughness;
      uniform vec3 waterColor;
      uniform vec3 sunColor;
      uniform vec3 sunDirection;
      uniform vec3 skyIrradiance;
      varying vec3 worldPosition;
      #include <common>
      #include <logdepthbuf_pars_fragment>

      void main() {
        #include <logdepthbuf_fragment>
        vec2 uv = worldPosition.xz * size * 0.025;
        vec3 first = texture2D(normalMap, uv + time * vec2(0.018, 0.011)).xyz * 2.0 - 1.0;
        vec3 second = texture2D(normalMap, uv * 1.73 + time * vec2(-0.013, 0.019)).xyz * 2.0 - 1.0;
        vec3 viewDirection = normalize(cameraPosition - worldPosition);
        float footprint = max(length(dFdx(uv)), length(dFdy(uv)));
        float detail = 1.0 / (1.0 + footprint * 4.0);
        vec2 slope = (first.xy + second.xy) * distortionScale * 0.07 * detail;
        vec3 normal = normalize(vec3(slope.x, 1.0, slope.y));
        vec3 reflected = reflect(-viewDirection, normal);
        float facing = clamp(dot(normal, viewDirection), 0.0, 1.0);
        float fresnel = 0.02 + 0.98 * pow(1.0 - facing, 5.0);

        vec3 ambient = skyIrradiance / PI;
        reflected.y = max(reflected.y, 0.001);
        reflected = normalize(reflected);
        vec3 sky = textureCube(skyMap, reflected).rgb;
        float skyBrightness = max(max(sky.r, sky.g), sky.b);
        // Preserve the sky's hue when lifting dim reflections. A neutral floor
        // would wash every time of day back to the same gray.
        vec3 fillColor = (sky + vec3(0.0001)) / (skyBrightness + 0.0001);
        sky += fillColor * minimumLight / PI;
        float sunAlignment = max(dot(reflected, sunDirection), 0.0);
        float sunAboveHorizon = smoothstep(-0.02, 0.04, sunDirection.y);

        float exponent = mix(512.0, 16.0, roughness);
        float glint = pow(sunAlignment, exponent) * (1.0 - roughness * 0.7);
        vec3 reflectedLight = sky + sunColor * glint * sunAboveHorizon;
        vec3 scatteredLight = waterColor * (ambient + sunColor * max(normal.y * sunDirection.y, 0.0));
        vec3 color = mix(scatteredLight, reflectedLight, fresnel);
        gl_FragColor = vec4(color, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
